/**
 * Insights hub — panoramica Analytics con KPI live e card di navigazione
 * verso le sotto-pagine (fatturato mensile, statistiche, quality, reporting,
 * previsione carico, MaeWorld per SuperAdmin).
 *
 * I KPI riusano le stesse queryKey delle pagine di dettaglio
 * (['monthly-summary'], ['yearly-trend'], ['statistics', '12m']): la cache
 * react-query è condivisa, quindi entrare nel dettaglio non rifà le chiamate.
 */
import { createFileRoute, Link, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { mw, MW_CARD_SHADOW } from '@/shared/mobile/tokens';
import { MaeShell, MaePageHead } from '@/shared/desktop/MaeShell';
import { MobileInsights } from '@/features/insights/mobile/MobileInsights';
import { useIsMobileShell } from '@/shared/mobile/useIsMobileShell';

/** Nomi colore (palette storica) → accent MaeDesign per le card insights. */
const INS_ACCENT: Record<string, { fg: string; iconBg: string; iconFg: string }> = {
  brand: { fg: mw.brand, iconBg: mw.brandTint, iconFg: mw.brand },
  teal: { fg: mw.success, iconBg: mw.successBg, iconFg: mw.success },
  green: { fg: mw.success, iconBg: mw.successBg, iconFg: mw.success },
  orange: { fg: mw.warning, iconBg: mw.warningBg, iconFg: mw.warning },
  blue: { fg: mw.accentText, iconBg: mw.accentTint, iconFg: mw.accentText },
  cyan: { fg: mw.accentText, iconBg: mw.accentTint, iconFg: mw.accentText },
  indigo: { fg: mw.accentText, iconBg: mw.accentTint, iconFg: mw.accentText },
  grape: { fg: mw.brand, iconBg: mw.brandTint, iconFg: mw.brand },
  gray: { fg: mw.ink900, iconBg: mw.surface2, iconFg: mw.ink500 },
};
const insAccent = (c: string) => INS_ACCENT[c] ?? INS_ACCENT.blue;
import {
  Card,
  Group,
  NumberFormatter,
  SimpleGrid,
  Stack,
  Text, rem,
} from '@mantine/core';
import { Sparkline } from '@mantine/charts';
import {
  IconBolt,
  IconBuildingFactory2,
  IconChartBar, IconCreditCard,
  IconChevronRight,
  IconClock,
  IconHelp,
  IconReceipt2,
  IconReport,
  IconTrendingUp,
  IconUsers,
  IconWorld,
} from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useIsSuperAdmin } from '@/shared/hooks/useIsSuperAdmin';
import { httpRequest } from '@/shared/api/client';
import { fetchList } from '@/shared/api/jsonapi/client';
import { InlineLoader } from '@/shared/components/LoadingState';
import { QueryErrorAlert } from '@/shared/components/QueryErrorAlert';
import type { Denormalized } from '@/shared/api/jsonapi/parser';

export const Route = createFileRoute('/_app/insights')({ component: InsightsLayout });

// ── Tipi response, allineati alle pagine di dettaglio ──

/** Shape di /api/insights/statistics (vedi features/insights/Statistics.tsx). */
interface StatsResponse {
  totalEmployees: number;
  activeProjects: number;
  hoursThisMonth: number;
  billableHoursThisMonth: number;
  monthlyTrend: Array<{ month: string; hours: number; billable: number }>;
}

/** Shape di /api/invoices/monthly-summary (vedi insights.monthly-invoices.tsx). */
interface Summary {
  totals: {
    billingHours: number;
    ordinaryHours: number;
    overtimeHours: number;
    projectsCount: number;
    billableProjects: number;
  };
}

/** Shape di /api/invoices/yearly-trend (vedi insights.monthly-invoices.tsx). */
interface Trend {
  series: Array<{ label: string; billingHours: number }>;
}

interface Ticket extends Denormalized {
  type: 'tickets';
  status?: string;
}

/** Layout: a /insights mostra l'hub (mobile dedicato o desktop), sui figli l'Outlet. */
function InsightsLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const isMobile = useIsMobileShell();
  const isChild = path.replace(/\/+$/, '') !== '/insights';
  if (isChild) return <Outlet />;
  return isMobile ? <MobileInsights /> : <InsightsHub />;
}

function InsightsHub() {
  const navigate = useNavigate();
  const isSuperAdmin = useIsSuperAdmin();

  const now = dayjs();
  const year = now.year();
  const month = now.month() + 1;

  // ── KPI: query già usate altrove (stesse queryKey ⇒ cache condivisa) ──
  const summaryQ = useQuery({
    queryKey: ['monthly-summary', year, month],
    queryFn: () => httpRequest<Summary>(`/api/invoices/monthly-summary?year=${year}&month=${month}`, { endpointType: 'plain' }),
    staleTime: 5 * 60_000,
  });
  const statsQ = useQuery({
    queryKey: ['statistics', '12m'],
    queryFn: () => httpRequest<StatsResponse>('/api/insights/statistics?range=12m', { endpointType: 'plain' }),
    staleTime: 5 * 60_000,
  });
  const ticketsQ = useQuery({
    queryKey: ['insights-open-tickets'],
    queryFn: () => fetchList<Ticket>('tickets', {
      filter: { status: { eq: 'open' } },
      page: { size: 100 },
    }),
    staleTime: 60_000,
  });
  // Trend 12 mesi solo per la sparkline della card "Fatturato mensile".
  const trendQ = useQuery({
    queryKey: ['yearly-trend'],
    queryFn: () => httpRequest<Trend>('/api/invoices/yearly-trend?monthsBack=12', { endpointType: 'plain' }),
    staleTime: 10 * 60_000,
  });

  const kpiLoading = summaryQ.isLoading || statsQ.isLoading || ticketsQ.isLoading;
  const allFailed = summaryQ.isError && statsQ.isError && ticketsQ.isError;

  const totals = summaryQ.data?.totals;
  const stats = statsQ.data;
  const openTickets = ticketsQ.data?.total ?? null;
  const billableRate = stats && stats.hoursThisMonth > 0
    ? (stats.billableHoursThisMonth / stats.hoursThisMonth) * 100
    : null;

  const invoiceSpark = (trendQ.data?.series ?? []).map((p) => p.billingHours);
  const hoursSpark = (stats?.monthlyTrend ?? []).map((m) => m.hours);

  return (
    <MaeShell>
      <MaePageHead
        icon={<IconChartBar size={22} />}
        tone="azzurro"
        title="Insights"
        sub={`Panoramica analytics: KPI di ${now.format('MMMM YYYY')} e accesso rapido a statistiche e report.`}
      />
      <Stack gap="lg">
      {/* KPI LIVE */}
      {allFailed ? (
        <QueryErrorAlert
          error={statsQ.error ?? summaryQ.error ?? ticketsQ.error}
          onRetry={() => {
            void summaryQ.refetch();
            void statsQ.refetch();
            void ticketsQ.refetch();
          }}
        />
      ) : kpiLoading ? (
        <InlineLoader label="Caricamento degli indicatori…" minHeight={140} />
      ) : (
        <SimpleGrid cols={{ base: 1, sm: 2, md: 3, lg: 5 }} spacing="md">
          <KpiCard
            icon={<IconReceipt2 size={24} />}
            label="Ore fatturabili (mese)"
            value={totals
              ? <NumberFormatter value={totals.billingHours} decimalScale={1} suffix="h" />
              : '—'}
            subline={totals
              ? `${totals.projectsCount} progetti · ${totals.billableProjects} fatturabili`
              : 'Dato non disponibile'}
            color="teal"
            onClick={() => void navigate({ to: '/insights/monthly-invoices' })}
          />
          <KpiCard
            icon={<IconBuildingFactory2 size={24} />}
            label="Progetti attivi"
            value={stats ? stats.activeProjects : '—'}
            subline={stats ? undefined : 'Dato non disponibile'}
            color="green"
            onClick={() => void navigate({ to: '/projects' })}
          />
          <KpiCard
            icon={<IconClock size={24} />}
            label="Ore mese"
            value={stats ? <NumberFormatter value={stats.hoursThisMonth} decimalScale={0} suffix="h" /> : '—'}
            subline={billableRate != null ? `${billableRate.toFixed(0)}% fatturabili` : 'Dato non disponibile'}
            color="orange"
            onClick={() => void navigate({ to: '/statistics' })}
          />
          <KpiCard
            icon={<IconUsers size={24} />}
            label="Dipendenti"
            value={stats ? stats.totalEmployees : '—'}
            subline={stats ? undefined : 'Dato non disponibile'}
            color="blue"
            onClick={() => void navigate({ to: '/workforce' })}
          />
          <KpiCard
            icon={<IconHelp size={24} />}
            label="Ticket aperti"
            value={openTickets ?? '—'}
            subline={openTickets == null ? 'Dato non disponibile' : undefined}
            color={openTickets != null && openTickets > 0 ? 'orange' : 'gray'}
            onClick={() => void navigate({ to: '/tickets' })}
          />
        </SimpleGrid>
      )}

      {/* NAVIGAZIONE SOTTO-PAGINE */}
      <div>
        <Text fw={600} mb="sm">Esplora</Text>
        <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
          <NavCard
            to="/insights/monthly-invoices"
            icon={IconReceipt2}
            color="teal"
            title="Fatturato mensile"
            description="Ore fatturabili per progetto, totali mese e trend degli ultimi 12 mesi."
            spark={invoiceSpark}
          />
          <NavCard
            to="/insights/subscriptions"
            icon={IconCreditCard}
            color="grape"
            title="Costi abbonamenti"
            description="Quanto costano gli abbonamenti dei dipendenti: per tipo, gruppo e mese, soglia fringe benefit, trattenute e budget."
          />
          <NavCard
            to="/statistics"
            icon={IconChartBar}
            color="blue"
            title="Statistiche"
            description="Trend operativo: ore, fatturabilità, assenze e smart working con confronto periodo."
            spark={hoursSpark}
          />
          <NavCard
            to="/quality"
            icon={IconBolt}
            color="grape"
            title="Quality"
            description="On-time delivery, accuratezza stime e quality score, personali e di team."
          />
          <NavCard
            to="/reporting"
            icon={IconReport}
            color="cyan"
            title="Reporting"
            description="Report con export CSV/XLS/PDF, preset salvabili e cronologia degli export."
          />
          <NavCard
            to="/project-forecast"
            icon={IconTrendingUp}
            color="orange"
            title="Previsione carico"
            description="Saturazione settimanale per progetto: capacità vs allocato e settimane critiche."
          />
          {isSuperAdmin && (
            <NavCard
              to="/maeworld"
              icon={IconWorld}
              color="indigo"
              title="MaeWorld"
              description="Panoramica di tutte le aziende per SuperAdmin: attività, ore e progetti per azienda."
            />
          )}
        </SimpleGrid>
      </div>
      </Stack>
    </MaeShell>
  );
}

/** Card KPI in identità MaeDesign: eyebrow + numero grande su accent + icon-box tinta. */
function KpiCard({ icon, label, value, subline, color, onClick }: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
  subline?: string;
  color: string;
  onClick?: () => void;
}) {
  const a = insAccent(color);
  return (
    <div
      onClick={onClick}
      className={onClick ? 'mw-pressable' : undefined}
      style={{ border: `1px solid ${mw.line}`, borderRadius: 16, background: mw.surface, boxShadow: MW_CARD_SHADOW, padding: '16px 18px', cursor: onClick ? 'pointer' : 'default' }}
    >
      <Group justify="space-between" wrap="nowrap" align="flex-start" gap={12}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text style={{ fontSize: 'var(--fs-compact)', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: mw.ink300 }}>{label}</Text>
          <Text style={{ fontSize: 'var(--fs-h2)', fontWeight: 700, lineHeight: 1.1, letterSpacing: '-0.02em', color: a.fg, marginTop: 4 }}>{value}</Text>
          {subline && <Text style={{ fontSize: 'var(--fs-compact)', color: mw.ink500, marginTop: 3 }} lineClamp={1}>{subline}</Text>}
        </div>
        <span style={{ width: 42, height: rem(42), borderRadius: 12, background: a.iconBg, color: a.iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{icon}</span>
      </Group>
    </div>
  );
}

/** Card di navigazione verso una sotto-pagina, con mini sparkline opzionale. */
function NavCard({ to, icon: Icon, color, title, description, spark }: {
  to: string;
  icon: typeof IconChartBar;
  color: string;
  title: string;
  description: string;
  spark?: number[];
}) {
  return (
    <Card
      component={Link}
      to={to}
      withBorder
      padding="lg"
      radius="lg"
      className="mw-pressable"
      style={{ color: 'inherit', textDecoration: 'none', borderColor: mw.line, boxShadow: MW_CARD_SHADOW }}
    >
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group wrap="nowrap" align="flex-start" gap="md">
          <span style={{ width: 48, height: 48, borderRadius: 13, background: insAccent(color).iconBg, color: insAccent(color).iconFg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Icon size={26} />
          </span>
          <div>
            <Text fw={600}>{title}</Text>
            <Text size="sm" c="dimmed" lineClamp={2}>{description}</Text>
          </div>
        </Group>
        <IconChevronRight size={18} style={{ opacity: 0.5, flexShrink: 0, marginTop: 4 }} />
      </Group>
      {spark && spark.length > 1 && (
        <Sparkline
          w="100%"
          h={36}
          mt="sm"
          data={spark}
          curveType="natural"
          color={color}
          fillOpacity={0.35}
          strokeWidth={1.5}
        />
      )}
    </Card>
  );
}
